import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Stethoscope, Search, Scissors, Pill, Flame, Heart,
  Clock, Calendar, MessageCircle, Phone, ArrowLeft, ArrowRight, ChevronRight
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const symptoms = [
  "Abnormal vaginal bleeding (between periods, after intercourse, or post-menopause) abdominal bloating or swelling",
  "Unusual vaginal discharge (watery, bloody, or foul-smelling)",
  "Pelvic pain or lower back pain",
  "Pain during intercourse",
  "Heavier or prolonged menstrual periods",
  "Fatigue or unexplained weight loss (in advanced stages)",
];

const whenToConsult = [
  "You experience abnormal vaginal bleeding or discharge",
  "An ovarian cyst or mass has been detected on ultrasound",
  "HPV (Human Papillomavirus) infection is detected",
  "You have persistent pelvic pain",
  "You have been diagnosed or suspected of cervical cancer",
];

const diagnostics = [
  { label: "", detail: "Pap smear test (routine screening)" },
  { label: "", detail: "HPV testing" },
  { label: "", detail: "Colposcopy (detailed examination of cervix)" },
  { label: "", detail: "Cervical biopsy" },
  { label: "", detail: "Imaging tests (MRI/CT scan) for staging" },
];

const treatments = [
  {
    icon: Scissors,
    title: "Surgery",
    description: "Surgery is commonly used in early-stage cervical cancer.",
    points: [
      "Hysterectomy (removal of uterus and cervix)",
      "Radical hysterectomy (removal of surrounding tissues)",
      "Fertility-preserving surgery (in selected early cases)",
      "Minimally invasive techniques may be used when appropriate"
    ],
  },
  {
    icon: Pill,
    title: "Radiation Therapy",
    description: "Radiation therapy is often used:",
    points: [
      "As a primary treatment in locally advanced cancer",
      "In combination with chemotherapy",
      "After surgery (if needed)",
      "It helps destroy cancer cells and reduce recurrence risk."
    ],
  },
  {
    icon: Heart,
    title: "Chemotherapy",
    description: "Chemotherapy may be used:",
    points: [
      "Along with radiation (chemoradiation)",
      "In advanced or metastatic cases",
      "It helps enhance the effectiveness of radiation and control disease spread.",
    ],
  },
  {
    icon: Heart,
    title: "Preventive Care (HPV Vaccination & Screening)",
    description: "Prevention is a key part of cervical cancer care:",
    points: [
      "HPV vaccination reduces risk",
      "Regular Pap smears detect early changes",
    ],
  },
];

const recoveryTimeline = [
  {
    phase: "After Surgery",
    items: [
      "Hospital stay: 3–7 days)",
      "Initial recovery: 2–3 weeks",
      "Full recovery: 4–6 weeks",
    ],
  },
  {
    phase: "During Radiation/Chemotherapy",
    items: [
      "Treatment duration: 6–8 weeks (approx)",
      "Side effects may include fatigue, skin irritation, and mild discomfort",
    ],
  },
  {
    phase: "Follow-Up Care",
    items: [
      "Regular monitoring is essential",
      "Pap smears and imaging may be advised periodically",
    ],
  },
];

const faqs = [
  {
    q: " Can cervical cancer be cured??",
    a: "Yes, cervical cancer is highly curable if detected early through screening and treated promptly.",
  },
  {
    q: "What causes cervical cancer?",
    a: "The primary cause is persistent infection with high-risk HPV (Human Papillomavirus).",
  },
  {
    q: "Is Pap smear necessary if I have no symptoms?",
    a: "Yes, Pap smear is a preventive screening test and should be done regularly even without symptoms.",
  },
  {
    q: " Can young women get cervical cancer?",
    a: "Yes, but it is more common in women over 30. HPV vaccination helps reduce risk.",
  },
  {
    q: "Will treatment affect fertility?",
    a: "In early cases, fertility-preserving options may be available. This depends on stage and treatment type.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

export default function OvarianCancerPage() {
  return (
    <div className="bg-[#F9F9F6] min-h-screen">
      {/* ─── STICKY HEADER ─── */}
      {/* <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F9F6]/80 border-b border-[#1E3F33]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 group" data-testid="service-header-logo">
            <span className="font-serif text-2xl md:text-3xl font-semibold text-[#1E3F33] tracking-tight">
              Dr. Neha Agarwal
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="service-back-home"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#4A6359] hover:text-[#1E3F33] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <a
              href="tel:+916291000829"
              data-testid="service-header-call"
              className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>
        </div>
      </header> */}

      {/* ─── HERO / INTRO ─── */}
      <section className="pt-28 md:pt-36 pb-16 md:pb-24" data-testid="service-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-[#4A6359] mb-8"
            data-testid="service-breadcrumb"
          >
            <Link to="/" className="hover:text-[#1E3F33] transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/#services" className="hover:text-[#1E3F33] transition-colors">Services</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#1E3F33] font-medium">Cervical Cancer Treatment</span>
          </motion.nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Gynecologic Oncology
              </p>
              <h1
                data-testid="service-page-title"
                style={{ lineHeight: "1.2" }}
                className="font-serif text-4xl sm:text-5xl sm:text-3xl tracking-tighter leading-[0.95] text-[#1E3F33] mb-6"
              >
                Cervical  Cancer Treatment in Kolkata – Advanced Care by Dr. Neha Agarwal
              </h1>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                Cervical  cancer is one of the most serious gynecologic cancers, often diagnosed at a later stage due to subtle early symptoms. Timely diagnosis and expert treatment are critical for improving outcomes.  </p>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                Dr. Neha Agarwal, an experienced gynecologic oncologist in Kolkata, provides comprehensive cervical cancer treatment using advanced surgical techniques, radiation coordination, and personalized care plans tailored to each patient.</p>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                Her approach focuses on early diagnosis, effective treatment, and preserving quality of life.</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/book-appointment"
                  data-testid="service-hero-book-btn"
                  className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Book Appointment
                </a>
                <a
                  href="https://wa.me/916291000829"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-hero-whatsapp-btn"
                  className="inline-flex items-center gap-2 border-2 border-[#1E3F33] text-[#1E3F33] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#1E3F33] hover:text-[#F9F9F6] transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </motion.div>

            {/* Quick Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8" data-testid="service-quick-info">
                <h3 className="font-serif text-2xl text-[#1E3F33] mb-5">
                  Quick Overview
                </h3>

                <div className="space-y-4">

                  {/* Specialist */}
                  <div className="flex items-start gap-3">
                    <Stethoscope className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Specialist
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Dr. Neha Agarwal, Gynecologic Oncologist
                      </p>
                    </div>
                  </div>

                  {/* Treatment */}
                  <div className="flex items-start gap-3">
                    <Scissors className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Treatment Options
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Surgery, Radiation Therapy, Chemotherapy & Preventive Care
                      </p>
                    </div>
                  </div>

                  {/* Screening & Diagnosis */}
                  <div className="flex items-start gap-3">
                    <Search className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Screening & Diagnosis
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Pap Smear, HPV Testing, Colposcopy, Cervical Biopsy
                      </p>
                    </div>
                  </div>

                  {/* Key Message */}
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Key Message
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Early screening and HPV prevention significantly reduce cervical cancer risk
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── SYMPTOMS ─── */}
      <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="service-symptoms-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-[#C29F60]" />
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#C29F60]">
                  Warning Signs
                </p>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-2xl" data-testid="service-symptoms-heading">
                Symptoms of Cervical Cancer
              </h2>
              <p className="text-base sm:text-lg text-[#8FA998] mt-4 max-w-2xl leading-relaxed">
                In early stages, cervical cancer may not cause noticeable symptoms. As the disease progresses, the following signs may appear:
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {symptoms.map((s, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-symptom-${i}`}
                  className="bg-[#2C5545] rounded-xl p-5 border border-[#8FA998]/15"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-[#C29F60]">{i + 1}</span>
                    </div>
                    <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">{s}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              variants={fadeUp}
              className="relative overflow-hidden rounded-2xl border-2 border-[#C29F60]/60 bg-gradient-to-r from-[#2C5545] to-[#234538] p-6 sm:p-7 shadow-[0_10px_40px_rgba(0,0,0,0.25)]"
            >
              {/* Subtle Glow */}
              <div className="absolute inset-0 bg-[#C29F60]/5 pointer-events-none" />

              <div className="relative flex items-start gap-4">

                {/* Warning Icon */}
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#C29F60] text-[#163828] font-bold text-xl shadow-lg">
                  !
                </div>

                {/* Text Content */}
                <div>
                  <p className="text-[#F4E7CC] text-lg sm:text-xl font-semibold leading-relaxed">
                    Any abnormal bleeding should always be evaluated by a{" "}
                    <span className="text-[#F6C978] font-bold">
                      specialist
                    </span>
                    .
                  </p>

                  <p className="mt-2 text-[#D7D7D7] text-sm sm:text-base">
                    Timely consultation helps identify underlying conditions early and ensures proper treatment.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── WHEN TO CONSULT ─── */}
      <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-consult-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Expert Guidance
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="service-consult-heading">
                When to Consult a Specialist
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                Early consultation allows timely diagnosis and significantly improves treatment success.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={stagger}
              className="space-y-0"
            >
              {whenToConsult.map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-consult-item-${i}`}
                  className="flex items-start gap-4 py-5 border-b border-[#D8DDD9] last:border-0"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#1E3F33]/10 flex items-center justify-center flex-shrink-0">
                    <ChevronRight className="w-4 h-4 text-[#1E3F33]" />
                  </div>
                  <p className="text-base sm:text-lg text-[#1E3F33] font-medium leading-relaxed">{item}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── DIAGNOSIS ─── */}
      <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="service-diagnosis-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-12"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
              Diagnosis & Screening
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="service-diagnosis-heading">
              Diagnostic methods include:
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {diagnostics.map((d, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`service-diagnostic-${i}`}
                className="bg-[#F9F9F6] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center mb-4">
                  <Search className="w-5 h-5 text-[#1E3F33]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-[#1E3F33] mb-1">{d.label}</h3>
                <p className="text-sm text-[#4A6359]">{d.detail}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── TREATMENT OPTIONS ─── */}
      <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-treatment-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-14"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
              Treatment Approach
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-3xl" data-testid="service-treatment-heading">
              Treatment Options for Cervical Cancer in Kolkata
            </h2>
            <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-2xl leading-relaxed">
              Treatment depends on the stage of cancer, overall health, and patient preferences            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {treatments.map((t, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`service-treatment-card-${i}`}
                className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center">
                    <t.icon className="w-6 h-6 text-[#1E3F33]" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33] leading-snug">{t.title}</h3>
                </div>
                <p className="text-sm sm:text-base text-[#4A6359] mb-4 leading-relaxed">{t.description}</p>
                <ul className="space-y-2.5">
                  {t.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8FA998] mt-2 flex-shrink-0" />
                      <span className="text-sm text-[#1E3F33] leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── RECOVERY TIMELINE ─── */}
      <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="service-recovery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-12"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
              Recovery Journey
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="service-recovery-heading">
              Recovery Timeline
            </h2>
            <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-xl leading-relaxed">
              Recovery varies depending on treatment
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {recoveryTimeline.map((phase, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`service-recovery-phase-${i}`}
                className="bg-[#F9F9F6] rounded-2xl p-7 border border-[#D8DDD9]"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-full bg-[#1E3F33] flex items-center justify-center">
                    <span className="text-xs font-bold text-[#F9F9F6]">{i + 1}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-[#1E3F33]">{phase.phase}</h3>
                </div>
                <ul className="space-y-3">
                  {phase.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <Clock className="w-4 h-4 text-[#8FA998] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#4A6359] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-faq-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Common Questions
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="service-faq-heading">
                Frequently Asked Questions
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                Find answers to common questions about ovarian cancer diagnosis, treatment options, and recovery.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <Accordion type="single" collapsible className="w-full" data-testid="service-faq-accordion">
                {faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="border-b border-[#D8DDD9] py-1"
                    data-testid={`service-faq-item-${i}`}
                  >
                    <AccordionTrigger
                      data-testid={`service-faq-trigger-${i}`}
                      className="font-serif text-lg sm:text-xl text-[#1E3F33] hover:no-underline py-5 text-left"
                    >
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#4A6359] text-base leading-relaxed pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="service-final-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-6">
              Take the First Step
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-3xl mx-auto mb-6" data-testid="service-final-cta-heading">
              Get Cervical Cancer Screening & Treatment in Kolkata
            </h2>
            <p className="text-base sm:text-lg text-[#8FA998] max-w-xl mx-auto leading-relaxed mb-10">
              Early detection can save lives. If you have symptoms or need screening, consult Dr. Neha Agarwal for expert cervical cancer care in Kolkata.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/book-appointment"
                data-testid="service-cta-book-btn"
                className="inline-flex items-center gap-2 bg-[#F9F9F6] text-[#1E3F33] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#E6E7E2] transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Book Appointment
              </a>
              <a
                href="tel:+916291000829"
                data-testid="service-cta-call-btn"
                className="inline-flex items-center gap-2 border-2 border-[#F9F9F6] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#F9F9F6] hover:text-[#1E3F33] transition-all"
              >
                <Phone className="w-4 h-4" />
                Get Second Opinion
              </a>
              <a
                href="https://wa.me/916291000829"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-cta-whatsapp-btn"
                className="inline-flex items-center gap-2 bg-[#2C5545] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#8FA998]/30 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── SEO FOOTER ─── */}
      <footer className="bg-[#1E3F33] border-t border-[#F9F9F6]/10 py-12" data-testid="service-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h3 className="font-serif text-lg text-[#F9F9F6]/50 mb-3">
            Cervical Cancer Specialist in Kolkata
          </h3>
          <p className="text-xs text-[#F9F9F6]/30 leading-relaxed max-w-3xl mb-6">
            Looking for cervical cancer treatment in Kolkata? Dr. Neha Agarwal provides advanced diagnosis, Pap smear screening, HPV management, and surgical care for patients across Mukundapur, EM Bypass, Garia, Kasba, Ballygunge, Salt Lake,Kankurgachi, Phoolbagan and nearby areas.
          </p>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-[#F9F9F6]/10">
            <p className="text-xs text-[#F9F9F6]/30">
              &copy; {new Date().getFullYear()} Dr. Neha Agarwal. All rights reserved.
            </p>
            <Link to="/" className="text-xs text-[#F9F9F6]/30 hover:text-[#F9F9F6]/60 transition-colors flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Back to Home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
